if ( typeof ShippingNotice !== 'function' ) {

  class ShippingNotice extends HTMLElement {

    connectedCallback() {
      this.init();
    }

    init() {

      const freeShippingThreshold = Number(this.getAttribute('data-free-shipping'));
      const cartTotal = Number(this.getAttribute('data-cart-total-amount'));
      const currencyRate = Shopify.currency.rate ? Number(Shopify.currency.rate) : 1;
      const convertedThreshold = Math.round(freeShippingThreshold * currencyRate);
      const freeShippingRemaining = cartTotal - convertedThreshold;
      let shippingMessage = '';
      let shippingSuccess = false

      if ( ! Number.isFinite(convertedThreshold) || convertedThreshold <= 0 || ! Number.isFinite(cartTotal) ) {
        this.innerHTML = '';
        return;
      }

      if ( freeShippingRemaining < 0 ) {
        shippingMessage = window.KROWN.settings.locales.shipping_notice_remaining_to_free.replace('{{ remaining_amount }}', this._formatPresentmentMoney(Math.abs(freeShippingRemaining)));

      } else {
        shippingMessage = window.KROWN.settings.locales.shipping_notice_eligible_for_free;
        shippingSuccess = true;
      }

      if ( this.hasAttribute('data-show-progress') ) {
        this._renderProgress({
          cartTotal,
          convertedThreshold,
          shippingSuccess
        });
      } else if ( this.hasAttribute('data-show-alert') ) {
        this.innerHTML = `
          <div class="alert alert--wide alert--${shippingSuccess ? 'success' : 'note'}">
            ${shippingMessage}
          </div>`;
      } else {
        this.innerHTML = shippingMessage;
      }

    }

    _renderProgress({ cartTotal, convertedThreshold, shippingSuccess }) {

      const progressValue = Math.min(Math.max(cartTotal, 0), convertedThreshold);
      const progressPercent = Math.min(100, Math.max(0, (progressValue / convertedThreshold) * 100));
      const currentAmount = this._formatPresentmentMoney(progressValue);
      const thresholdAmount = this._formatPresentmentMoney(convertedThreshold);
      const remainingAmount = this._formatPresentmentMoney(Math.max(convertedThreshold - cartTotal, 0));
      const progressText = this._replaceTokens(this.getAttribute('data-progress-value'), {
        current_amount: currentAmount,
        threshold_amount: thresholdAmount
      });
      const headline = shippingSuccess
        ? this.getAttribute('data-success')
        : this._replaceTokens(this.getAttribute('data-remaining'), { remaining_amount: remainingAmount });
      const supportingText = shippingSuccess
        ? this.getAttribute('data-success-note')
        : this._replaceTokens(this.getAttribute('data-threshold'), { threshold_amount: thresholdAmount });
      const progressLabel = this.getAttribute('data-progress-label');
      const icon = shippingSuccess ? this._checkIcon() : this._truckIcon();

      this.innerHTML = `
        <div class="cart-shipping-goal${shippingSuccess ? ' cart-shipping-goal--success' : ''}">
          <div class="cart-shipping-goal__top">
            <span class="cart-shipping-goal__icon" aria-hidden="true">${icon}</span>
            <div class="cart-shipping-goal__copy">
              <p class="cart-shipping-goal__headline">${this._escapeHTML(headline)}</p>
              <p class="cart-shipping-goal__support">${this._escapeHTML(supportingText)}</p>
            </div>
          </div>
          <div
            class="cart-shipping-goal__track"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="${(convertedThreshold / 100).toFixed(2)}"
            aria-valuenow="${(progressValue / 100).toFixed(2)}"
            aria-label="${this._escapeHTML(progressLabel)}"
            aria-valuetext="${this._escapeHTML(progressText)}"
          >
            <span class="cart-shipping-goal__fill" style="--cart-shipping-progress: ${progressPercent.toFixed(2)}%"></span>
          </div>
        </div>`;

    }

    _replaceTokens(template, replacements) {

      return Object.entries(replacements).reduce(
        (value, [token, replacement]) => value.replace(`{{ ${token} }}`, replacement),
        template || ''
      );

    }

    _escapeHTML(value) {

      return String(value || '').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      })[character]);

    }

    _truckIcon() {

      return `
        <svg viewBox="0 0 24 24" fill="none" focusable="false">
          <path d="M3.5 6.5h10v10h-10zM13.5 10h3.3l3.2 3.2v3.3h-6.5z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"></path>
          <circle cx="7" cy="17" r="1.8" stroke="currentColor" stroke-width="1.7"></circle>
          <circle cx="17" cy="17" r="1.8" stroke="currentColor" stroke-width="1.7"></circle>
        </svg>`;

    }

    _checkIcon() {

      return `
        <svg viewBox="0 0 24 24" fill="none" focusable="false">
          <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.7"></circle>
          <path d="m8 12.2 2.5 2.5 5.6-5.8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>`;

    }

    _formatPresentmentMoney(cents) {

      const currencyIsoCode = this.getAttribute('data-currency-iso-code');
      const shopCurrencyIsoCode = this.getAttribute('data-shop-currency-iso-code');

      if ( ! currencyIsoCode || ! shopCurrencyIsoCode || currencyIsoCode === shopCurrencyIsoCode ) {
        return this._formatMoney(cents, KROWN.settings.shop_money_format);
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currencyIsoCode
        }).format(cents / 100);
      } catch (error) {
        return `${this._formatMoney(cents, '{{ amount }}')} ${currencyIsoCode}`;
      }

    }
    
    _formatMoney(cents, format) {

			if (typeof cents === 'string') {
				cents = cents.replace('.', '');
			}
	
			let value = '';
			const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
			const formatString = format || moneyFormat;
	
			function formatWithDelimiters(number, precision, thousands, decimal) {
				thousands = thousands || ',';
				decimal = decimal || '.';
	
				if (isNaN(number) || number === null) {
					return 0;
				}
	
				number = (number / 100.0).toFixed(precision);
	
				const parts = number.split('.');
				const dollarsAmount = parts[0].replace(
					/(\d)(?=(\d\d\d)+(?!\d))/g,
					'$1' + thousands
				);
				const centsAmount = parts[1] ? decimal + parts[1] : '';
	
				return dollarsAmount + centsAmount;
			}
	
			switch (formatString.match(placeholderRegex)[1]) {
				case 'amount':
					value = formatWithDelimiters(cents, 2);
					break;
				case 'amount_no_decimals':
					value = formatWithDelimiters(cents, 0);
					break;
				case 'amount_with_comma_separator':
					value = formatWithDelimiters(cents, 2, '.', ',');
					break;
				case 'amount_no_decimals_with_comma_separator':
					value = formatWithDelimiters(cents, 0, '.', ',');
					break;
				case 'amount_no_decimals_with_space_separator':
					value = formatWithDelimiters(cents, 0, ' ');
					break;
				case 'amount_with_apostrophe_separator':
					value = formatWithDelimiters(cents, 2, "'");
					break;
			}
	
			return formatString.replace(placeholderRegex, value);
	
		}

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}
